import type { SupportedLocale } from "@/lib/i18n/config";

import enApply from "@/lib/i18n/messages/en/apply.json";
import enAuth from "@/lib/i18n/messages/en/auth.json";
import enBlog from "@/lib/i18n/messages/en/blog.json";
import enBuilder from "@/lib/i18n/messages/en/builder.json";
import enCommon from "@/lib/i18n/messages/en/common.json";
import enFind from "@/lib/i18n/messages/en/find.json";
import enInterview from "@/lib/i18n/messages/en/interview.json";
import enLegal from "@/lib/i18n/messages/en/legal.json";
import enMarketing from "@/lib/i18n/messages/en/marketing.json";
import enSeo from "@/lib/i18n/messages/en/seo.json";
import enTools from "@/lib/i18n/messages/en/tools.json";
import enWorkspace from "@/lib/i18n/messages/en/workspace.json";
import esApply from "@/lib/i18n/messages/es/apply.json";
import esAuth from "@/lib/i18n/messages/es/auth.json";
import esBlog from "@/lib/i18n/messages/es/blog.json";
import esBuilder from "@/lib/i18n/messages/es/builder.json";
import esCommon from "@/lib/i18n/messages/es/common.json";
import esFind from "@/lib/i18n/messages/es/find.json";
import esInterview from "@/lib/i18n/messages/es/interview.json";
import esLegal from "@/lib/i18n/messages/es/legal.json";
import esMarketing from "@/lib/i18n/messages/es/marketing.json";
import esSeo from "@/lib/i18n/messages/es/seo.json";
import esTools from "@/lib/i18n/messages/es/tools.json";
import esWorkspace from "@/lib/i18n/messages/es/workspace.json";

export const MESSAGE_DOMAINS = [
  "common",
  "auth",
  "marketing",
  "builder",
  "workspace",
  "apply",
  "interview",
  "find",
  "blog",
  "legal",
  "tools",
  "seo",
] as const;

export type MessageDomain = (typeof MESSAGE_DOMAINS)[number];
export type MessageCatalog = Record<string, string>;
export type DomainMessageCatalog = Record<MessageDomain, MessageCatalog>;

export const messageCatalogs: Record<SupportedLocale, DomainMessageCatalog> = {
  en: {
    common: enCommon,
    auth: enAuth,
    marketing: enMarketing,
    builder: enBuilder,
    workspace: enWorkspace,
    apply: enApply,
    interview: enInterview,
    find: enFind,
    blog: enBlog,
    legal: enLegal,
    tools: enTools,
    seo: enSeo,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    marketing: esMarketing,
    builder: esBuilder,
    workspace: esWorkspace,
    apply: esApply,
    interview: esInterview,
    find: esFind,
    blog: esBlog,
    legal: esLegal,
    tools: esTools,
    seo: esSeo,
  },
};

const flattenDomainCatalog = (
  catalog: DomainMessageCatalog,
  locale: SupportedLocale
): MessageCatalog => {
  const flatCatalog: MessageCatalog = {};

  for (const domain of MESSAGE_DOMAINS) {
    for (const [key, value] of Object.entries(catalog[domain])) {
      if (flatCatalog[key] !== undefined) {
        throw new Error(
          `Duplicate i18n key "${key}" found while flattening the "${locale}" catalog.`
        );
      }

      flatCatalog[key] = value;
    }
  }

  return flatCatalog;
};

export const flatMessageCatalogs: Record<SupportedLocale, MessageCatalog> = {
  en: flattenDomainCatalog(messageCatalogs.en, "en"),
  es: flattenDomainCatalog(messageCatalogs.es, "es"),
};

export const getFlatMessageCatalog = (
  locale: SupportedLocale
): MessageCatalog => flatMessageCatalogs[locale];
